import { type EmployeeLevel, type FundingRound } from "@prisma/client";
import { value } from "~/components/helpers";
import { type Input, type MarketOrCompareInput } from "~/components/market-data/MarketDataBrowser";
import { type NullableAuthenticatedUser } from "~/services/auth/fetchAuthenticatedUser";
import { formatFundingRounds } from "~/services/funding";
import { getHeadcountLabel, getHeadcountRange, mapRangeToHeadcount } from "~/services/headcount";

export const getRangeFromInput = (input: MarketOrCompareInput) => {
  if (input.headcount !== null) return input.headcount;

  if (input.size.min !== null || input.size.max !== null) return mapRangeToHeadcount(input.size);

  return null;
};

export const getSizeFromInput = (input: MarketOrCompareInput) => {
  if (input.size.min !== null || input.size.max !== null) return input.size;

  if (input.headcount !== null) return getHeadcountRange(input.headcount);

  return { min: null, max: null };
};

export const getDefaultLevels = (user: NullableAuthenticatedUser): EmployeeLevel[] => {
  if (!user || !user.permissions.allowedLevels.length) {
    return ["JUNIOR", "INTERMEDIATE", "SENIOR"];
  }

  return user.permissions.allowedLevels.slice(0, 3);
};

export const getDefaultBenchmarkLevelIds = (user: NullableAuthenticatedUser | null): number[] => {
  if (!user || !user.permissions.allowedBenchmarkLevels.length) {
    return [];
  }

  //we're slicing to 3 to get default levels in the same way as getDefaultLevels & have three levels in the default input.
  return user.permissions.allowedBenchmarkLevels.slice(0, 3).map((level) => level.id);
};

export const mapInputToFiltersPreset = (input: Input) => {
  const levelOptions = input.levels.map((level) => ({ name: level, id: level }));

  //always a unique value !== input.market.size
  const headcountOptions = input.market.headcount
    ? { name: getHeadcountLabel(input.market.headcount), id: input.market.headcount }
    : null;

  const sizeOptions = value(() => {
    if (input.market.size.min !== null || input.market.size.max !== null) {
      return true;
    }

    return false;
  });

  const fundingRoundsOptions =
    input.market.fundingRounds.length > 0
      ? input.market.fundingRounds.map((fundingRound) => ({
          name: formatFundingRounds(fundingRound),
          id: fundingRound as FundingRound,
        }))
      : null;

  const labelOptions =
    input.market.labels.length > 0 ? input.market.labels.map((label) => ({ name: label.name, id: label.id })) : null;

  //we have to flatten for the industry options while it's still funky
  const industryOptions =
    [...(input.market.industries || []), ...(input.market.industry ? [input.market.industry] : [])].length > 0
      ? input.market.industries.map((industry) => ({ name: industry.name, id: industry.id }))
      : null;

  return {
    levelOptions,
    headcountOptions,
    sizeOptions,
    fundingRoundsOptions,
    labelOptions,
    industryOptions,
  };
};
